import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import {
  AlertTriangle,
  Bell,
  Database,
  Download,
  Binoculars,
  Eye,
  EyeOff,
  Globe,
  Hourglass,
  Snowflake,
  KeyRound,
  Lock,
  Monitor,
  Server,
  ShieldAlert,
  ShieldCheck,
  Skull,
  Trash2,
  Unlock,
  UserCheck,
  KeySquare,
} from 'lucide-react';
import { gauntlet, ApiError } from '../lib/api';
import { useSession } from '../store/session';
import { cx, formatDate, formatFiat, timeAgo } from '../lib/format';
import { Badge, Button, Callout, CopyButton, Input, Modal, Panel } from '../components/ui';

const TABS = [
  { id: 'general', label: 'General', Icon: Globe },
  { id: 'security', label: 'Security', Icon: ShieldCheck },
  { id: 'privacy', label: 'Privacy', Icon: EyeOff },
  { id: 'recovery', label: 'Recovery', Icon: KeyRound },
  { id: 'alerts', label: 'Alerts', Icon: Bell },
  { id: 'data', label: 'Data & tax', Icon: Database },
  { id: 'network', label: 'Network', Icon: Server },
  { id: 'api', label: 'API', Icon: KeySquare },
  { id: 'admin', label: 'Admin', Icon: ShieldAlert, adminOnly: true },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function SettingsPage() {
  const [tab, setTab] = useState<TabId>('general');
  const isAdmin = useSession((s) => s.isAdmin);

  // The Admin tab is hidden from non-administrators, but that is presentation
  // only — every privileged route re-checks on the server.
  const visible = TABS.filter((t) => !('adminOnly' in t && t.adminOnly) || isAdmin);

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold tracking-tight">Settings</h1>

      <div className="flex flex-wrap gap-1.5 border-b border-hairline pb-3">
        {visible.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cx(
              'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors',
              tab === id
                ? 'border-brass/50 bg-brass-wash text-brass'
                : 'border-hairline text-ink-dim hover:text-ink',
            )}
          >
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'general' && <GeneralTab />}
      {tab === 'security' && <SecurityTab />}
      {tab === 'privacy' && <PrivacyTab />}
      {tab === 'recovery' && <RecoveryTab />}
      {tab === 'alerts' && <AlertsTab />}
      {tab === 'data' && <DataTab />}
      {tab === 'network' && <NetworkTab />}
      {tab === 'api' && <ApiTab />}
      {tab === 'admin' && isAdmin && <AdminTab />}
    </div>
  );
}

/* ------------------------------------------------------------------ security */

function SecurityTab() {
  const totpEnabled = useSession((s) => s.totpEnabled);
  const queryClient = useQueryClient();

  const [enrolment, setEnrolment] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [totpError, setTotpError] = useState<string | null>(null);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwMessage, setPwMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(
    null,
  );

  const [duressPin, setDuressPin] = useState('');
  const [duressAdvice, setDuressAdvice] = useState<string | null>(null);

  const { data: sessions } = useQuery({ queryKey: ['sessions'], queryFn: () => gauntlet.sessions() });
  const { data: audit } = useQuery({ queryKey: ['audit'], queryFn: () => gauntlet.auditLog() });
  const { data: duress } = useQuery({ queryKey: ['duress'], queryFn: () => gauntlet.duressStatus() });

  const beginTotp = useMutation({ mutationFn: () => gauntlet.totpBegin(), onSuccess: setEnrolment });
  const confirmTotp = useMutation({
    mutationFn: () => gauntlet.totpConfirm(code),
    onSuccess: async () => {
      setEnrolment(null);
      setCode('');
      setTotpError(null);
      await queryClient.invalidateQueries();
      useSession.setState({ totpEnabled: true });
    },
    onError: (e) => setTotpError(e instanceof ApiError ? e.message : 'That code did not verify.'),
  });

  const changePassword = useMutation({
    mutationFn: () => gauntlet.changePassword(oldPassword, newPassword),
    onSuccess: () => {
      setOldPassword('');
      setNewPassword('');
      setPwMessage({ tone: 'success', text: 'Sign-in password changed.' });
    },
    onError: (e) =>
      setPwMessage({
        tone: 'danger',
        text: e instanceof ApiError ? e.message : 'Could not change the password.',
      }),
  });

  const setDuress = useMutation({
    mutationFn: () => gauntlet.configureDuress(duressPin, null),
    onSuccess: async (r) => {
      setDuressPin('');
      setDuressAdvice(r.advice);
      await queryClient.invalidateQueries({ queryKey: ['duress'] });
    },
  });

  return (
    <div className="space-y-5">
      {/* Sign-in password. The vault password is separate and deliberately not
          changeable here — it re-encrypts seeds and belongs on the wallet. */}
      <Panel title="Sign-in password">
        <div className="space-y-3">
          <p className="text-sm text-ink-dim">
            This unlocks the web interface. It is <strong>not</strong> your vault password — that one
            encrypts your seed phrases and is set per wallet.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Input
              type="password"
              label="Current password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              autoComplete="current-password"
            />
            <Input
              type="password"
              label="New password"
              hint="At least 10 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          {pwMessage && <Callout tone={pwMessage.tone}>{pwMessage.text}</Callout>}
          <Button
            variant="primary"
            icon={<KeyRound size={14} />}
            disabled={!oldPassword || newPassword.length < 10}
            loading={changePassword.isPending}
            onClick={() => changePassword.mutate()}
          >
            Change password
          </Button>
        </div>
      </Panel>

      {/* Two-factor */}
      <Panel
        title="Two-factor authentication"
        action={
          <Badge tone={totpEnabled ? 'gain' : 'neutral'}>{totpEnabled ? 'enabled' : 'off'}</Badge>
        }
      >
        {totpEnabled ? (
          <Callout tone="success">
            Two-factor is on. A code from your authenticator app is required at every sign-in.
          </Callout>
        ) : enrolment ? (
          <div className="space-y-4">
            <p className="text-sm text-ink-dim">
              Scan this with your authenticator app, then enter the six-digit code it shows.
            </p>
            <div className="flex justify-center">
              <div className="rounded-xl bg-white p-4">
                <QRCodeSVG value={enrolment.uri} size={176} level="M" marginSize={0} />
              </div>
            </div>
            <div className="space-y-1 text-center">
              <p className="label-xs">Or enter this secret manually</p>
              <p className="addr">{enrolment.secret}</p>
              <CopyButton value={enrolment.secret} label="Copy secret" />
            </div>
            <Input
              label="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="000000"
              mono
              error={totpError}
            />
            <div className="flex gap-2">
              <Button full onClick={() => setEnrolment(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                full
                loading={confirmTotp.isPending}
                disabled={code.length !== 6}
                onClick={() => confirmTotp.mutate()}
              >
                Verify &amp; enable
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-ink-dim">
              Adds a second factor to signing in. Note that it protects the web interface, not your
              wallet keys — those are protected by each wallet's own vault password.
            </p>
            <Button
              variant="primary"
              icon={<ShieldCheck size={15} />}
              loading={beginTotp.isPending}
              onClick={() => beginTotp.mutate()}
            >
              Set up two-factor
            </Button>
          </div>
        )}
      </Panel>

      {/* Duress PIN */}
      <Panel
        title="Duress PIN"
        action={
          <Badge tone={duress?.configured ? 'gain' : 'neutral'}>
            {duress?.configured ? 'configured' : 'not set'}
          </Badge>
        }
      >
        <div className="space-y-3">
          <Callout tone="warn" title="Read this before setting one">
            A duress PIN opens a decoy view instead of your real wallet. It protects you from someone
            standing over your shoulder — it does <strong>not</strong> protect you from someone who
            has your seed phrase, and lying to law enforcement about it is a crime in some countries.
          </Callout>
          <div className="flex flex-wrap items-end gap-2">
            <Input
              label="Duress PIN"
              className="w-40"
              value={duressPin}
              onChange={(e) => setDuressPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              inputMode="numeric"
              placeholder="••••••"
              mono
            />
            <Button
              variant="danger"
              icon={<Skull size={14} />}
              disabled={duressPin.length < 4}
              loading={setDuress.isPending}
              onClick={() => setDuress.mutate()}
            >
              {duress?.configured ? 'Replace PIN' : 'Set PIN'}
            </Button>
          </div>
          {duressAdvice && <Callout tone="info">{duressAdvice}</Callout>}
          {duress?.triggeredAt && (
            <Callout tone="danger" title="This PIN has been used">
              The duress PIN was entered on {formatDate(duress.triggeredAt)}. If that was not you,
              move your funds now.
            </Callout>
          )}
        </div>
      </Panel>

      {/* Sessions */}
      <Panel title="Signed-in sessions" action={<Badge>{sessions?.sessions.length ?? 0}</Badge>}>
        <ul className="divide-y divide-hairline">
          {(sessions?.sessions ?? []).map((s) => (
            <li key={s.id} className="flex items-center gap-3 py-2.5 text-sm">
              <Monitor size={15} className="shrink-0 text-ink-faint" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px]">{s.user_agent ?? 'Unknown device'}</p>
                <p className="text-xs text-ink-faint">
                  {s.ip_address ?? 'no ip'} · last seen {timeAgo(s.last_seen)}
                </p>
              </div>
              {s.id === sessions?.current && <Badge tone="gain">this device</Badge>}
            </li>
          ))}
          {(sessions?.sessions.length ?? 0) === 0 && (
            <li className="py-4 text-center text-sm text-ink-faint">No other sessions.</li>
          )}
        </ul>
      </Panel>

      {/* Audit log */}
      <Panel title="Recent account activity" padded={false}>
        <ul className="max-h-80 divide-y divide-hairline overflow-y-auto">
          {(audit?.entries ?? []).map((entry, i) => (
            <li key={i} className="flex items-center gap-3 px-5 py-2 text-[13px]">
              <span
                className={cx(
                  'size-1.5 shrink-0 rounded-full',
                  entry.success ? 'bg-gain' : 'bg-loss',
                )}
              />
              <span className="w-40 shrink-0 truncate font-medium">{entry.action}</span>
              <span className="min-w-0 flex-1 truncate text-ink-faint">
                {entry.detail ?? entry.ip_address ?? ''}
              </span>
              <span className="shrink-0 text-xs text-ink-faint">{timeAgo(entry.created_at)}</span>
            </li>
          ))}
          {(audit?.entries.length ?? 0) === 0 && (
            <li className="px-5 py-6 text-center text-sm text-ink-faint">Nothing logged yet.</li>
          )}
        </ul>
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ recovery */

function RecoveryTab() {
  const queryClient = useQueryClient();
  const [showPlan, setShowPlan] = useState(false);
  const [showLock, setShowLock] = useState(false);
  const [showWatch, setShowWatch] = useState(false);
  const [showAddress, setShowAddress] = useState(false);

  const { data: plans } = useQuery({ queryKey: ['inheritance'], queryFn: () => gauntlet.inheritance() });
  const { data: locks } = useQuery({ queryKey: ['timelocks'], queryFn: () => gauntlet.timeLocks() });
  const { data: wallets } = useQuery({ queryKey: ['wallets'], queryFn: () => gauntlet.wallets() });

  const checkIn = useMutation({
    mutationFn: (id: number) => gauntlet.inheritanceCheckIn(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inheritance'] }),
  });
  const deletePlan = useMutation({
    mutationFn: (id: number) => gauntlet.deleteInheritance(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inheritance'] }),
  });
  const releaseLock = useMutation({
    mutationFn: (id: number) => gauntlet.releaseTimeLock(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['timelocks'] }),
  });
  const deleteLock = useMutation({
    mutationFn: (id: number) => gauntlet.deleteTimeLock(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['timelocks'] }),
  });

  return (
    <div className="space-y-5">
      {/* Dead-man's switch */}
      <Panel
        title="Inheritance"
        action={
          <Button size="sm" icon={<UserCheck size={13} />} onClick={() => setShowPlan(true)}>
            New plan
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-dim">
            {plans?.explainer ??
              'A dead-man’s switch. If you stop checking in, an encrypted envelope becomes claimable by the person you name.'}
          </p>

          {(plans?.plans ?? []).map((plan) => (
            <div key={plan.id} className="rounded-lg border border-hairline p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{plan.heir_name}</span>
                <Badge
                  tone={
                    plan.status === 'active' ? 'gain' : plan.status === 'grace' ? 'warn' : 'loss'
                  }
                >
                  {plan.status}
                </Badge>
                <span className="ml-auto text-xs text-ink-faint">
                  checked in {timeAgo(plan.last_check_in)}
                </span>
              </div>

              {/* The countdown is the whole point of the feature, so it is a bar
                  rather than a number buried in prose. */}
              <div className="mt-2.5">
                <div className="flex justify-between text-xs text-ink-faint">
                  <span>{plan.daysUntilGrace} days until grace period</span>
                  <span>{plan.daysUntilRelease} until release</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-raised">
                  <div
                    className={cx(
                      'h-full rounded-full',
                      plan.daysUntilGrace > 7 ? 'bg-gain' : 'bg-warn',
                    )}
                    style={{
                      width: `${Math.max(2, Math.min(100, (plan.daysUntilGrace / Math.max(1, plan.check_in_days)) * 100))}%`,
                    }}
                  />
                </div>
              </div>

              {plan.envelope_hint && (
                <p className="mt-2 text-xs text-ink-faint">Hint for heir: {plan.envelope_hint}</p>
              )}

              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  loading={checkIn.isPending && checkIn.variables === plan.id}
                  onClick={() => checkIn.mutate(plan.id)}
                >
                  I'm still here
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 size={13} />}
                  onClick={() => deletePlan.mutate(plan.id)}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}

          {(plans?.plans.length ?? 0) === 0 && (
            <p className="py-3 text-center text-sm text-ink-faint">No inheritance plan set up.</p>
          )}
        </div>
      </Panel>

      {/* Time locks */}
      <Panel
        title="Time locks"
        action={
          <Button size="sm" icon={<Hourglass size={13} />} onClick={() => setShowLock(true)}>
            New lock
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-dim">
            Stop yourself spending. A soft lock hides the balance from the send screen; it is a
            speed bump against your own impulses, not a consensus rule.
          </p>
          {(locks?.locks ?? []).map((lock) => (
            <div
              key={lock.id}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-hairline p-3"
            >
              {lock.unlocked ? (
                <Unlock size={15} className="text-gain" />
              ) : (
                <Lock size={15} className="text-brass" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{lock.name}</p>
                <p className="text-xs text-ink-faint">
                  {lock.chain_id} · {lock.kind} ·{' '}
                  {lock.unlocked
                    ? 'unlocked'
                    : `${lock.daysLeft} days left, opens ${formatDate(lock.unlock_at)}`}
                </p>
              </div>
              {lock.unlocked ? (
                <Button
                  size="sm"
                  variant="primary"
                  loading={releaseLock.isPending && releaseLock.variables === lock.id}
                  onClick={() => releaseLock.mutate(lock.id)}
                >
                  Release
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 size={13} />}
                  onClick={() => deleteLock.mutate(lock.id)}
                >
                  Cancel
                </Button>
              )}
            </div>
          ))}
          {(locks?.locks.length ?? 0) === 0 && (
            <p className="py-3 text-center text-sm text-ink-faint">No time locks.</p>
          )}
        </div>
      </Panel>

      {/* Watch-only */}
      <Panel
        title="Watch-only wallet"
        action={
          <Button size="sm" icon={<Eye size={13} />} onClick={() => setShowWatch(true)}>
            Import xpub
          </Button>
        }
      >
        <p className="text-sm text-ink-dim">
          Track a hardware wallet or a cold-storage seed without importing any private key. Gauntlet
          derives addresses from the extended public key and follows the balance; it cannot spend.
        </p>
      </Panel>

      <Panel
        title="Watch plain addresses"
        action={
          <Button size="sm" icon={<Binoculars size={13} />} onClick={() => setShowAddress(true)}>
            Add addresses
          </Button>
        }
      >
        <p className="text-sm text-ink-dim">
          No key needed — paste the addresses themselves. Useful for keeping an eye on cold storage,
          following a donation address, or filling the app with real data to see how it behaves.
          Only these exact addresses are watched; nothing further is derived.
        </p>
      </Panel>

      <WatchAddressModal open={showAddress} onClose={() => setShowAddress(false)} />
      <NewPlanModal open={showPlan} onClose={() => setShowPlan(false)} wallets={wallets?.wallets ?? []} />
      <NewLockModal open={showLock} onClose={() => setShowLock(false)} />
      <WatchOnlyModal open={showWatch} onClose={() => setShowWatch(false)} />
    </div>
  );
}

function NewPlanModal({
  open,
  onClose,
  wallets,
}: {
  open: boolean;
  onClose: () => void;
  wallets: { id: number; name: string }[];
}) {
  const queryClient = useQueryClient();
  const [heirName, setHeirName] = useState('');
  const [heirContact, setHeirContact] = useState('');
  const [secret, setSecret] = useState('');
  const [hint, setHint] = useState('');
  const [checkInDays, setCheckInDays] = useState('90');
  const [walletId, setWalletId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      gauntlet.createInheritance({
        walletId: walletId ?? wallets[0]?.id ?? 0,
        heirName,
        heirContact: heirContact || undefined,
        // The server stores this blob verbatim and never reads it. Encoding is
        // done here so nothing plain-text-looking crosses the wire by accident.
        envelopeBase64: btoa(unescape(encodeURIComponent(secret))),
        envelopeHint: hint || undefined,
        checkInDays: Number(checkInDays) || 90,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['inheritance'] });
      setHeirName('');
      setSecret('');
      setHint('');
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create the plan.'),
  });

  return (
    <Modal open={open} onClose={onClose} title="Set up inheritance">
      <div className="space-y-3">
        <Callout tone="warn" title="What you put in the envelope matters">
          Whatever you write below is what your heir eventually receives. Most people put recovery
          instructions and where to find the seed phrase — <strong>not</strong> the seed itself,
          since this server would then hold it.
        </Callout>

        {wallets.length > 1 && (
          <label className="block">
            <span className="label-xs">Wallet</span>
            <select
              value={walletId ?? wallets[0]?.id}
              onChange={(e) => setWalletId(Number(e.target.value))}
              className="mt-1 h-10 w-full rounded-lg border border-hairline bg-abyss px-3 text-sm"
            >
              {wallets.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <Input label="Heir's name" value={heirName} onChange={(e) => setHeirName(e.target.value)} />
        <Input
          label="How to reach them"
          hint="Optional — email or phone, stored as written"
          value={heirContact}
          onChange={(e) => setHeirContact(e.target.value)}
        />
        <label className="block">
          <span className="label-xs">Message for your heir</span>
          <textarea
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            rows={4}
            placeholder="The seed phrase is in the safe at… the vault password hint is…"
            className="mt-1 w-full rounded-lg border border-hairline bg-abyss p-3 text-sm"
          />
        </label>
        <Input
          label="Public hint"
          hint="Shown before release, so keep it vague"
          value={hint}
          onChange={(e) => setHint(e.target.value)}
        />
        <Input
          label="Check in every (days)"
          type="number"
          value={checkInDays}
          onChange={(e) => setCheckInDays(e.target.value)}
        />

        {error && <Callout tone="danger">{error}</Callout>}

        <Button
          variant="primary"
          full
          disabled={!heirName || !secret}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          Create plan
        </Button>
      </div>
    </Modal>
  );
}

function NewLockModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [chainId, setChainId] = useState('bitcoin');
  const [days, setDays] = useState('365');

  const create = useMutation({
    mutationFn: () =>
      gauntlet.createTimeLock({
        name,
        chainId,
        unlockAt: Date.now() + Number(days) * 86_400_000,
        kind: 'soft',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['timelocks'] });
      setName('');
      onClose();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="New time lock">
      <div className="space-y-3">
        <Input
          label="What is this for"
          placeholder="Don't touch until 2027"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input label="Chain" value={chainId} onChange={(e) => setChainId(e.target.value.trim())} />
        <Input
          label="Lock for (days)"
          type="number"
          value={days}
          onChange={(e) => setDays(e.target.value)}
        />
        <Callout tone="info">
          This is a soft lock stored in Gauntlet's database. It will not stop someone who has your
          seed phrase and another wallet app — it stops <em>you</em>, on a bad day.
        </Callout>
        <Button
          variant="primary"
          full
          disabled={!name || !chainId}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          Lock it
        </Button>
      </div>
    </Modal>
  );
}

function WatchOnlyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [chainId, setChainId] = useState('bitcoin');
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);

  /* Preview before committing. Proton and Sparrow both tell you to check the
     first few derived addresses match — which was impossible until now, and is
     the only way to catch a wrong script type before funds are missed. */
  const debounced = useDebounced(key, 400);
  const { data: preview, error: previewError } = useQuery({
    queryKey: ['watch-preview', chainId, debounced],
    queryFn: () => gauntlet.previewWatchWallet({ chainId, xpub: debounced }),
    enabled: debounced.trim().length > 20,
    retry: false,
  });

  const create = useMutation({
    mutationFn: () => gauntlet.createWatchWallet({ name, chainId, xpub: key }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setKey('');
      setName('');
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'That key was not accepted.'),
  });

  const SOURCE_LABEL = {
    xpub: 'plain extended key',
    slip132: 'SLIP-132 key',
    descriptor: 'output descriptor',
  } as const;

  const TYPE_LABEL: Record<string, string> = {
    p2wpkh: 'Native SegWit — bc1q (BIP-84)',
    p2tr: 'Taproot — bc1p (BIP-86)',
    'p2sh-p2wpkh': 'Wrapped SegWit — 3… (BIP-49)',
    p2pkh: 'Legacy — 1… (BIP-44)',
  };

  return (
    <Modal open={open} onClose={onClose} title="Import a watch-only wallet" width="max-w-lg">
      <div className="space-y-3">
        <Input label="Name" placeholder="Proton" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Chain" value={chainId} onChange={(e) => setChainId(e.target.value.trim())} />

        <label className="block">
          <span className="label-xs">Extended public key or output descriptor</span>
          <textarea
            value={key}
            onChange={(e) => setKey(e.target.value.trim())}
            rows={4}
            placeholder={"zpub… / ypub… / xpub…\nor  wpkh([73c5da0a/84'/0'/0']xpub…/0/*)#checksum"}
            className="addr mt-1 w-full rounded-lg border border-hairline bg-abyss p-3 text-xs"
          />
          <span className="mt-1 block text-xs text-ink-faint">
            Paste whichever your other wallet gives you. The script type is read from the key
            itself, so there is nothing to choose and nothing to get wrong.
          </span>
        </label>

        {previewError && (
          <Callout tone="danger" title="Cannot read that">
            {previewError instanceof ApiError ? previewError.message : 'Unrecognised format.'}
          </Callout>
        )}

        {preview && (
          <div className="space-y-2 rounded-lg border border-hairline bg-abyss p-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge tone="gain">{SOURCE_LABEL[preview.source]}</Badge>
              {preview.addressType && (
                <Badge tone="brass">{TYPE_LABEL[preview.addressType] ?? preview.addressType}</Badge>
              )}
              {preview.originPath && <Badge>m/{preview.originPath}</Badge>}
              {preview.fingerprint && <Badge>{preview.fingerprint}</Badge>}
            </div>
            <p className="label-xs">First addresses this will watch</p>
            <ol className="space-y-0.5">
              {preview.addresses.map((address, i) => (
                <li key={address} className="addr flex gap-2 text-[11px]">
                  <span className="num w-4 shrink-0 text-ink-faint">{i}</span>
                  <span className="truncate">{address}</span>
                </li>
              ))}
            </ol>
            <p className="text-[11px] leading-relaxed text-ink-faint">
              Check these against the wallet you exported from before importing. If they do not
              match, the script type is wrong and Gauntlet would watch an empty account.
            </p>
          </div>
        )}

        {error && <Callout tone="danger">{error}</Callout>}

        <Callout tone="info">
          An extended public key reveals every address you will ever use on this account. Anyone who
          has it can watch your entire balance forever — treat it as private even though it cannot
          spend.
        </Callout>

        <Button
          variant="primary"
          full
          disabled={!name || !preview}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          Import
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Watch bare addresses.
 *
 * Separate from the xpub path on purpose: there is no key, so nothing is
 * derived and what you paste is exactly what is followed. Each address is
 * validated for the chosen chain before anything is written, because a wallet
 * holding an address from the wrong network syncs to nothing and looks broken
 * rather than wrong.
 */
function WatchAddressModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [chainId, setChainId] = useState('bitcoin');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addresses = text
    .split(/[\s,]+/)
    .map((a) => a.trim())
    .filter(Boolean);

  const create = useMutation({
    mutationFn: () => gauntlet.createWatchAddresses({ name, chainId, addresses }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setText('');
      setName('');
      setError(null);
      onClose();
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : 'Those addresses were not accepted.'),
  });

  return (
    <Modal open={open} onClose={onClose} title="Watch addresses" width="max-w-lg">
      <div className="space-y-3">
        <Input
          label="Name"
          placeholder="Cold storage"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input label="Chain" value={chainId} onChange={(e) => setChainId(e.target.value.trim())} />

        <label className="block">
          <span className="label-xs">Addresses</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            placeholder={'bc1q…\nbc1p…\n3…'}
            className="addr mt-1 w-full rounded-lg border border-hairline bg-abyss p-3 text-xs"
          />
          <span className="mt-1 block text-xs text-ink-faint">
            One per line, or separated by spaces or commas.
            {addresses.length > 0 && ` ${addresses.length} found.`}
          </span>
        </label>

        {error && <Callout tone="danger">{error}</Callout>}

        <Callout tone="info">
          Watching is read-only and derives nothing further. If the wallet you are watching hands
          out a fresh address per payment, this will only ever see the ones you pasted — import its
          extended key instead to follow all of them.
        </Callout>

        <Button
          variant="primary"
          full
          disabled={!name || addresses.length === 0}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          Watch {addresses.length > 0 ? `${addresses.length} address${addresses.length === 1 ? '' : 'es'}` : ''}
        </Button>
      </div>
    </Modal>
  );
}

/** Delay a fast-changing value so a paste does not fire a request per keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/* -------------------------------------------------------------------- alerts */

const ALERT_TYPES = [
  { id: 'price_above', label: 'Price rises above', needsThreshold: true },
  { id: 'price_below', label: 'Price falls below', needsThreshold: true },
  { id: 'price_change', label: 'Price moves by %', needsThreshold: true },
  { id: 'incoming_tx', label: 'Anything arrives', needsThreshold: false },
  { id: 'outgoing_tx', label: 'Anything leaves', needsThreshold: false },
  { id: 'balance_below', label: 'Balance drops below', needsThreshold: true },
] as const;

function AlertsTab() {
  const queryClient = useQueryClient();
  const [type, setType] = useState<(typeof ALERT_TYPES)[number]['id']>('price_below');
  const [chainId, setChainId] = useState('bitcoin');
  const [threshold, setThreshold] = useState('');
  const [note, setNote] = useState('');

  const { data } = useQuery({ queryKey: ['alerts'], queryFn: () => gauntlet.alerts() });

  const create = useMutation({
    mutationFn: () =>
      gauntlet.createAlert({
        type,
        chainId: chainId || undefined,
        threshold: threshold || undefined,
        note: note || undefined,
      }),
    onSuccess: async () => {
      setThreshold('');
      setNote('');
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => gauntlet.deleteAlert(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  });

  const needsThreshold = ALERT_TYPES.find((t) => t.id === type)?.needsThreshold ?? false;

  return (
    <div className="space-y-5">
      <Panel title="New alert">
        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-3">
            <label className="block">
              <span className="label-xs">When</span>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as typeof type)}
                className="mt-1 h-10 w-full rounded-lg border border-hairline bg-abyss px-3 text-sm"
              >
                {ALERT_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <Input label="Chain" value={chainId} onChange={(e) => setChainId(e.target.value.trim())} />
            {needsThreshold && (
              <Input
                label={type === 'price_change' ? 'Percent' : 'Amount'}
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                mono
              />
            )}
          </div>
          <Input
            label="Note"
            hint="Optional — shown when the alert fires"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            variant="primary"
            icon={<Bell size={14} />}
            disabled={needsThreshold && !threshold}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create alert
          </Button>
        </div>
      </Panel>

      <Panel title="Your alerts" padded={false}>
        <ul className="divide-y divide-hairline">
          {(data?.alerts ?? []).map((alert) => (
            <li key={alert.id} className="flex items-center gap-3 px-5 py-3">
              <Bell size={15} className="shrink-0 text-brass" />
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  {ALERT_TYPES.find((t) => t.id === alert.type)?.label ?? alert.type}
                  {alert.threshold ? ` ${alert.threshold}` : ''}
                  {alert.chain_id ? ` · ${alert.chain_id}` : ''}
                </p>
                {alert.note && <p className="text-xs text-ink-faint">{alert.note}</p>}
              </div>
              {alert.triggered_at ? (
                <Badge tone="warn">fired {timeAgo(alert.triggered_at)}</Badge>
              ) : (
                <Badge tone="gain">armed</Badge>
              )}
              <button
                onClick={() => remove.mutate(alert.id)}
                className="rounded p-1 text-ink-faint transition-colors hover:text-loss"
                aria-label="Delete alert"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
          {(data?.alerts.length ?? 0) === 0 && (
            <li className="px-5 py-8 text-center text-sm text-ink-faint">
              No alerts yet. They are evaluated on each price refresh and surface in the app.
            </li>
          )}
        </ul>
      </Panel>
    </div>
  );
}

/* ---------------------------------------------------------------------- data */

function DataTab() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear - 1);

  const { data: unrealised } = useQuery({
    queryKey: ['tax-unrealised'],
    queryFn: () => gauntlet.taxUnrealised(),
  });
  const { data: report, refetch, isFetching } = useQuery({
    queryKey: ['tax-report', year],
    queryFn: () => gauntlet.taxReport(year),
    enabled: false,
  });

  const downloadLabels = async () => {
    const jsonl = await gauntlet.exportLabels();
    const blob = new Blob([typeof jsonl === 'string' ? jsonl : JSON.stringify(jsonl)], {
      type: 'application/jsonlines',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gauntlet-labels.jsonl';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <Panel title="Unrealised gains">
        <div className="space-y-2">
          <p className="text-sm text-ink-dim">
            What you would owe if you sold everything today. Nothing here is a taxable event yet.
          </p>
          <ul className="divide-y divide-hairline">
            {(unrealised?.positions ?? []).map((p, i) => (
              <li key={i} className="flex items-center gap-3 py-2 text-sm">
                <span className="w-16 shrink-0 font-medium">{p.symbol}</span>
                <span className="num min-w-0 flex-1 truncate text-ink-faint">{p.amount}</span>
                <span className={cx('num', Number(p.gain) >= 0 ? 'text-gain' : 'text-loss')}>
                  {Number(p.gain) >= 0 ? '+' : ''}
                  {p.gain}
                </span>
                <span
                  className={cx(
                    'num w-16 text-right text-xs',
                    p.gainPercent >= 0 ? 'text-gain' : 'text-loss',
                  )}
                >
                  {p.gainPercent >= 0 ? '+' : ''}
                  {p.gainPercent.toFixed(1)}%
                </span>
              </li>
            ))}
            {(unrealised?.positions.length ?? 0) === 0 && (
              <li className="py-4 text-center text-sm text-ink-faint">
                No positions with a cost basis yet.
              </li>
            )}
          </ul>
        </div>
      </Panel>

      <Panel title="Capital gains report">
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <Input
              label="Tax year"
              type="number"
              className="w-32"
              value={String(year)}
              onChange={(e) => setYear(Number(e.target.value))}
            />
            <Button variant="primary" loading={isFetching} onClick={() => void refetch()}>
              Generate
            </Button>
          </div>

          {report && (
            <div className="grid gap-3 sm:grid-cols-3">
              <Figure label="Short term" value={report.shortTerm} />
              <Figure label="Long term" value={report.longTerm} />
              <Figure label="Total" value={report.total} accent />
            </div>
          )}

          <Callout tone="info">
            Computed with the Python cost-basis engine bundled with Gauntlet (FIFO, LIFO and HIFO
            available on the CLI). It is a starting point for your accountant, not tax advice.
          </Callout>
        </div>
      </Panel>

      <Panel title="Export">
        <div className="space-y-3">
          <p className="text-sm text-ink-dim">
            Labels export as BIP-329 JSONL, the format Sparrow, Nunchuk and BlueWallet all read. Your
            annotations move with you.
          </p>
          <Button icon={<Download size={14} />} onClick={() => void downloadLabels()}>
            Export labels (BIP-329)
          </Button>
        </div>
      </Panel>
    </div>
  );
}

function Figure({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="panel p-3">
      <p className="label-xs">{label}</p>
      <p className={cx('num mt-1 text-lg font-semibold', accent && 'text-brass')}>{value}</p>
    </div>
  );
}

/* ------------------------------------------------------------------- network */

function NetworkTab() {
  const isAdmin = useSession((s) => s.isAdmin);
  const username = useSession((s) => s.username);

  const { data: health } = useQuery({ queryKey: ['health'], queryFn: () => gauntlet.health() });
  const { data: endpointData } = useQuery({
    queryKey: ['endpoints'],
    queryFn: () => gauntlet.endpoints(),
  });

  return (
    <div className="space-y-5">
      <Panel title="Where balances are read from">
        <div className="space-y-4">
          <Callout tone="info" title="The only real privacy fix">
            By default Gauntlet reads balances from public indexers. Those services can see which
            addresses you ask about, and can link them together by IP. Pointing a chain at your own
            node is the only configuration that avoids this — self-hosting the wallet alone does not.
          </Callout>

          {(endpointData?.endpoints.length ?? 0) > 0 ? (
            <ul className="divide-y divide-hairline rounded-lg border border-hairline">
              {endpointData?.endpoints.map((endpoint) => (
                <li key={String(endpoint['id'])} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="w-28 shrink-0 truncate text-ink-dim">
                    {String(endpoint['chain_id'])}
                  </span>
                  <span className="addr min-w-0 flex-1 truncate">{String(endpoint['url'])}</span>
                  <Badge>{String(endpoint['kind'])}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-faint">
              No custom nodes configured. Every chain is using its default public indexer.
            </p>
          )}

          {/* Configuration lives on the Admin tab, because a node change affects
              every account on the instance, not just the person making it. */}
          <p className="text-xs text-ink-faint">
            {isAdmin
              ? 'Add or change nodes on the Admin tab.'
              : `Only the instance administrator can change these. You are signed in as ${username ?? 'this account'}.`}
          </p>
        </div>
      </Panel>

      <Panel title="About this instance">
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <Row label="Version" value={health?.version ?? '—'} />
          <Row label="Signed in as" value={username ?? '—'} />
          <Row label="Administrator" value={isAdmin ? 'you' : 'someone else'} />
          <Row label="Chains in registry" value={String(health?.chains.total ?? 0)} />
        </dl>
      </Panel>

      <Callout tone="warn" title="Nothing here leaves your network">
        <span className="inline-flex items-center gap-1.5">
          <ShieldAlert size={13} />
          Gauntlet is bound to your LAN. If you ever expose it to the internet, put it behind a VPN
          or reverse proxy with TLS first — there is no rate limit that survives a determined
          attacker on a wallet.
        </span>
      </Callout>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-hairline pb-2">
      <dt className="text-ink-faint">{label}</dt>
      <dd className="num text-ink">{value}</dd>
    </div>
  );
}

export { AlertTriangle };


/* ------------------------------------------------------------------- privacy */

const SEVERITY_TONE = { high: 'danger', medium: 'warn', low: 'info' } as const;

/**
 * Privacy health.
 *
 * Most wallets treat privacy as a fixed property of the software. It is not —
 * it degrades with use, and none of that degradation shows up in a balance.
 */
function PrivacyTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['privacy'],
    queryFn: () => gauntlet.privacyReport(),
  });

  const freeze = useMutation({
    mutationFn: () => gauntlet.freezeDust(),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (isLoading || !data) return <Panel><p className="py-6 text-center text-sm text-ink-faint">Checking…</p></Panel>;

  const hasDust = data.findings.some((f) => f.id === 'dust');
  const tone =
    data.score >= 90 ? 'text-gain' : data.score >= 70 ? 'text-brass' : data.score >= 45 ? 'text-warn' : 'text-loss';

  return (
    <div className="space-y-5">
      <Panel>
        <div className="flex flex-wrap items-center gap-5">
          <div className="text-center">
            <p className={cx('num text-4xl font-semibold', tone)}>{data.score}</p>
            <p className="label-xs mt-1 capitalize">{data.grade}</p>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-ink-dim">
              Checked {data.checkedAddresses} addresses and {data.checkedUtxos} unspent coins. This
              is a prompt to look, not a measurement — a perfect score is not the same as being
              anonymous, and using your own node matters more than anything on this list.
            </p>
          </div>
        </div>
      </Panel>

      {data.findings.length === 0 ? (
        <Callout tone="success" title="Nothing to flag">
          No address reuse, no dust and no unusual clustering. Keep using a fresh receive address
          for each payment.
        </Callout>
      ) : (
        data.findings.map((finding) => (
          <Callout
            key={finding.id}
            tone={SEVERITY_TONE[finding.severity]}
            title={finding.title}
          >
            <span className="block">{finding.detail}</span>
            {finding.action && (
              <span className="mt-1.5 block text-ink-dim">{finding.action}</span>
            )}
          </Callout>
        ))
      )}

      {hasDust && (
        <Button
          variant="primary"
          icon={<Snowflake size={14} />}
          loading={freeze.isPending}
          onClick={() => freeze.mutate()}
        >
          Freeze all dust coins
        </Button>
      )}

      {freeze.isSuccess && (
        <Callout tone="success">
          Froze {freeze.data.frozen} coin{freeze.data.frozen === 1 ? '' : 's'}. They will never be
          selected automatically — you can still spend them deliberately from the Send screen.
        </Callout>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------- api */

/**
 * Personal API tokens: programmatic access for payment requests.
 * The token is shown exactly once; only its hash is stored.
 */
function ApiTab() {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [fresh, setFresh] = useState<{ token: string; row: { id: number; label: string } } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['tokens'],
    queryFn: () => gauntlet.tokens(),
  });
  const tokens = data?.tokens ?? [];

  const create = useMutation({
    mutationFn: () => gauntlet.createToken(label),
    onSuccess: (result) => {
      setFresh(result);
      setLabel('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create the token.'),
  });

  const revoke = useMutation({
    mutationFn: (id: number) => gauntlet.revokeToken(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tokens'] }),
  });

  return (
    <div className="space-y-5">
      <Panel title="API tokens">
        <p className="text-sm text-ink-dim">
          Tokens let your own code create and read payment requests — a website
          checkout, a bot, a script. They can never unlock or sign your wallet.
          {' '}<a href="/docs" target="_blank" rel="noreferrer noopener" className="text-brass no-underline hover:underline">
            API docs
          </a>
        </p>

        <div className="mt-3 flex gap-2">
          <Input
            placeholder="e.g. my-website-checkout"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="flex-1"
          />
          <Button
            variant="primary"
            disabled={!label.trim()}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create token
          </Button>
        </div>
        {error && <div className="mt-2"><Callout tone="danger">{error}</Callout></div>}

        {fresh && (
          <div className="mt-3 space-y-2">
            <Callout tone="warn" title="Copy this now — it is shown once">
              <div className="space-y-2">
                <p className="addr break-all rounded-lg border border-hairline bg-void p-2 text-xs">
                  {fresh.token}
                </p>
                <p className="text-xs text-ink-faint">
                  Use it as <span className="addr">Authorization: Bearer {fresh.token.slice(0, 8)}…</span>
                  {" "}against /api/requests. It is hashed on the server; it cannot be shown again.
                </p>
              </div>
            </Callout>
          </div>
        )}

        {tokens.length > 0 && (
          <ul className="mt-4 divide-y divide-hairline rounded-lg border border-hairline">
            {tokens.map((token) => (
              <li key={token.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate font-medium">{token.label}</span>
                <span className="addr text-xs text-ink-faint">{token.prefix}…</span>
                <span className="text-[11px] text-ink-faint">
                  {token.last_used_at
                    ? `used ${timeAgo(token.last_used_at)}`
                    : 'never used'}
                </span>
                <Button
                  size="sm"
                  variant="danger"
                  loading={revoke.isPending && revoke.variables === token.id}
                  onClick={() => {
                    if (window.confirm(`Delete the token "${token.label}"? Anything using it will stop working immediately.`)) {
                      revoke.mutate(token.id);
                    }
                  }}
                >
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Callout tone="info" title="What tokens can do">
        Create and read payment requests for your account, and nothing else —
        no wallet access, no signing, no settings. A token is a key to your
        payment links, not to your funds.
      </Callout>
    </div>
  );
}

/* ------------------------------------------------------------------- general */

/**
 * The currencies worth putting first.
 *
 * CoinGecko supports around 60, including crypto quote units like `btc` and
 * `sats`. Listing all of them alphabetically buries the handful anyone wants,
 * so the common ones are promoted and the rest stay reachable below.
 */
const COMMON_CURRENCIES = [
  ['usd', 'US Dollar'],
  ['gbp', 'Pound Sterling'],
  ['eur', 'Euro'],
  ['cad', 'Canadian Dollar'],
  ['aud', 'Australian Dollar'],
  ['jpy', 'Japanese Yen'],
  ['inr', 'Indian Rupee'],
  ['chf', 'Swiss Franc'],
  ['nzd', 'New Zealand Dollar'],
  ['zar', 'South African Rand'],
  ['sek', 'Swedish Krona'],
  ['brl', 'Brazilian Real'],
] as const;

function GeneralTab() {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: () => gauntlet.settings() });
  const { data: available } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => gauntlet.currencies(),
    staleTime: Infinity,
  });
  const { data: portfolio } = useQuery({
    queryKey: ['portfolio'],
    queryFn: () => gauntlet.portfolio(),
  });

  const current = settings?.settings['currency'] ?? portfolio?.currency ?? 'usd';

  const save = useMutation({
    mutationFn: (currency: string) => gauntlet.saveSettings({ currency }),
    onSuccess: async () => {
      setSaved(true);
      // Every fiat figure in the app is derived server-side, so everything has
      // to be refetched — not just the portfolio.
      await queryClient.invalidateQueries();
    },
  });

  // Anything CoinGecko supports that is not already promoted above.
  const promoted = new Set(COMMON_CURRENCIES.map(([code]) => code as string));
  const others = (available?.currencies ?? []).filter((c) => !promoted.has(c)).sort();

  return (
    <div className="space-y-5">
      <Panel title="Display currency">
        <div className="space-y-3">
          <p className="text-sm text-ink-dim">
            What every balance, limit and price is shown in. Rates come from CoinGecko; your coins
            are unaffected — this changes the lens, not the holding.
          </p>

          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {COMMON_CURRENCIES.map(([code, label]) => (
              <button
                key={code}
                onClick={() => save.mutate(code)}
                className={cx(
                  'rounded-lg border px-3 py-2 text-left transition-colors',
                  current === code
                    ? 'border-brass/50 bg-brass-wash text-brass'
                    : 'border-hairline text-ink-dim hover:border-hairline-bright',
                )}
              >
                <span className="block text-sm font-medium uppercase">{code}</span>
                <span className="block text-[11px] text-ink-faint">{label}</span>
              </button>
            ))}
          </div>

          {others.length > 0 && (
            <label className="block">
              <span className="label-xs">Something else</span>
              <select
                value={promoted.has(current) ? '' : current}
                onChange={(e) => e.target.value && save.mutate(e.target.value)}
                className="mt-1 h-10 w-full rounded-lg border border-hairline bg-abyss px-3 text-sm"
              >
                <option value="">Choose from {others.length} more…</option>
                {others.map((code) => (
                  <option key={code} value={code}>
                    {code.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
          )}

          {saved && (
            <Callout tone="success">
              Now showing amounts in <strong>{current.toUpperCase()}</strong>.
            </Callout>
          )}
        </div>
      </Panel>

      <Panel title="Right now">
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <Row label="Portfolio value" value={portfolio ? formatFiat(portfolio.totalValue, portfolio.currency) : '—'} />
          <Row label="Quote currency" value={(portfolio?.currency ?? current).toUpperCase()} />
        </dl>
      </Panel>
    </div>
  );
}


/* --------------------------------------------------------------------- admin */

/**
 * Instance administration.
 *
 * Only rendered for the administrator, but that is cosmetic. Everything here
 * hits a route guarded by `requireAdmin`, which re-derives the answer from the
 * server environment on every request and writes a denial to the audit log —
 * so hiding the tab is a courtesy, not the control.
 */
function AdminTab() {
  const queryClient = useQueryClient();
  const username = useSession((s) => s.username);
  const [endpointChain, setEndpointChain] = useState('');
  const [endpointUrl, setEndpointUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: health } = useQuery({ queryKey: ['health'], queryFn: () => gauntlet.health() });
  const { data: endpointData } = useQuery({
    queryKey: ['endpoints'],
    queryFn: () => gauntlet.endpoints(),
  });
  const { data: audit } = useQuery({ queryKey: ['audit'], queryFn: () => gauntlet.auditLog() });

  const addEndpoint = useMutation({
    mutationFn: () =>
      gauntlet.addEndpoint({
        chainId: endpointChain,
        url: endpointUrl,
        kind: endpointUrl.includes('/api/v2')
          ? 'blockbook'
          : endpointUrl.includes('/api')
            ? 'esplora'
            : 'rpc',
      }),
    onSuccess: async () => {
      setEndpointChain('');
      setEndpointUrl('');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['endpoints'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not add that node.'),
  });

  const denials = (audit?.entries ?? []).filter((e) => e.action === 'admin.denied');

  return (
    <div className="space-y-5">
      <Callout tone="success" title="You are the instance administrator">
        Signed in as <strong>{username}</strong>. This is set by{' '}
        <span className="addr">GAUNTLET_ADMIN_USER</span> in the server environment — read fresh on
        every request, never stored as a row and never accepted from the browser. There is no API
        that can grant it; changing it needs access to the host and a restart.
      </Callout>

      <Panel title="Custom nodes" >
        <div className="space-y-4">
          <p className="text-sm text-ink-dim">
            Instance-wide: this changes where <em>every</em> account's balances are read from, which
            is why it is restricted. Pointing a chain at your own node is also the only change that
            actually fixes the privacy leak of using public indexers.
          </p>

          <div className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
            <Input
              placeholder="chain id (e.g. bitcoin)"
              value={endpointChain}
              onChange={(e) => setEndpointChain(e.target.value.trim())}
            />
            <Input
              placeholder="https://your-node.local/api"
              value={endpointUrl}
              onChange={(e) => setEndpointUrl(e.target.value.trim())}
            />
            <Button
              variant="primary"
              icon={<Server size={14} />}
              disabled={!endpointChain || !endpointUrl}
              loading={addEndpoint.isPending}
              onClick={() => addEndpoint.mutate()}
            >
              Add
            </Button>
          </div>

          {error && <Callout tone="danger">{error}</Callout>}

          {(endpointData?.endpoints.length ?? 0) > 0 ? (
            <ul className="divide-y divide-hairline rounded-lg border border-hairline">
              {endpointData?.endpoints.map((endpoint) => (
                <li key={String(endpoint['id'])} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="w-28 shrink-0 truncate text-ink-dim">
                    {String(endpoint['chain_id'])}
                  </span>
                  <span className="addr min-w-0 flex-1 truncate">{String(endpoint['url'])}</span>
                  <Badge>{String(endpoint['kind'])}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-ink-faint">
              No custom nodes. Every chain is using its default public indexer.
            </p>
          )}
        </div>
      </Panel>

      <Panel title="Refused privileged actions" action={<Badge tone={denials.length ? 'warn' : 'gain'}>{denials.length}</Badge>}>
        {denials.length === 0 ? (
          <p className="text-sm text-ink-dim">
            Nobody has been turned away from an administrator-only route. Attempts are recorded here
            whether they come from the interface or straight from the API.
          </p>
        ) : (
          <ul className="divide-y divide-hairline">
            {denials.slice(0, 10).map((entry, i) => (
              <li key={i} className="flex items-center gap-3 py-2 text-[13px]">
                <span className="size-1.5 shrink-0 rounded-full bg-loss" />
                <span className="min-w-0 flex-1 truncate text-ink-faint">{entry.detail}</span>
                <span className="shrink-0 text-xs text-ink-faint">{timeAgo(entry.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Instance">
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <Row label="Version" value={health?.version ?? '—'} />
          <Row label="Chains in registry" value={String(health?.chains.total ?? 0)} />
          <Row label="Built-in tokens" value={String(health?.chains.tokens ?? 0)} />
          <Row
            label="Uptime"
            value={
              health
                ? `${Math.floor(health.uptimeSec / 3600)}h ${Math.floor((health.uptimeSec % 3600) / 60)}m`
                : '—'
            }
          />
        </dl>
      </Panel>
    </div>
  );
}
